import styled from "@emotion/styled"

import { Link } from "components/core/Link"
import { minTabletMediaQuery } from "theme/media-queries"
import { rgba } from "utils/cssUtils"

export const Container = styled.div`
  background-color: ${p => rgba(p.theme.colors.primaryExtremelyLight, 0.7)};
  margin: 0 auto;
  padding: 60px 16px;

  ${minTabletMediaQuery} {
    padding: 80px 0;
  }
`

export const Subcontainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`

export const Title = styled.h2`
  ${p => p.theme.typography.special.size_28px.bold()}
  color: ${p => p.theme.colors.primaryMedium};
  margin-top: 0;

  ${minTabletMediaQuery} {
    ${p => p.theme.typography.special.size_32px.bold()}
  }
`

export const Text = styled.p`
  ${p => p.theme.typography.standard.size_14px.normal()}
  color: ${p => rgba(p.theme.colors.primaryMedium, 0.7)};
  margin: 0 auto 40px;
  max-width: 375px;

  ${minTabletMediaQuery} {
    ${p => p.theme.typography.standard.size_16px.normal()}
  }
`

export const SocialMedia = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  margin: 30px 0;
  justify-content: center;

  svg {
    cursor: pointer;
    opacity: 1;

    &:hover {
      opacity: 0.9;
    }
  }
`

export const LinkItem = styled(Link)`
  ${p => p.theme.typography.standard.size_16px.normal()}
  background-color: transparent;
  color: ${p => rgba(p.theme.colors.primaryMedium, 0.7)};
  cursor: pointer;
  text-decoration: none;
  transition: background-color 240ms linear;
  border: 1px solid ${p => rgba(p.theme.colors.primaryMedium, 0.15)};
  border-radius: 50px;
  padding: 12px;
  align-items: center;
  display: flex;
  justify-content: center;

  :hover {
    background-color: ${p => p.theme.colors.primaryExtraLight};
  }

  :visited {
    color: ${p => rgba(p.theme.colors.primaryMedium, 0.7)};
  }

  > svg {
    height: 1.5rem;
    width: 1.5rem;
  }
`

export const LinkItemButton = LinkItem.withComponent("button")

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  border-color: 1px red solid;
  gap: 10px;

  ${minTabletMediaQuery} {
    flex-direction: row;
  }
`

export const Input = styled.input`
  ${p => p.theme.typography.standard.size_15px.normal()}
  color: ${p => p.theme.colors.neutralExtraDark};
  background-color: transparent;
  max-height: 50px;
  max-width: 800px;
  width: 100%;
  padding: 12px 15px;
  border: 1px solid ${p => rgba(p.theme.colors.primaryMedium, 0.15)};
  border-radius: 2px;

  ::placeholder {
    color: ${p => rgba(p.theme.colors.primaryMedium, 0.5)};
  }

  :focus {
    outline: transparent;
    border: 1px solid ${p => p.theme.colors.accentMedium};
  }

  ${minTabletMediaQuery} {
    max-width: 225px;
  }
`

export const InputErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 100%;

  ${minTabletMediaQuery} {
    width: auto;
  }
`

export const InputSubcontainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  background-color: transparent;
  max-height: 50px;
  max-width: 800px;
  width: 100%;
  padding: 12px 15px;
  border: 1px solid ${p => p.theme.colors.failureMedium};
  border-radius: 2px;
  gap: 10px;
  align-items: center;

  :focus-within {
    border: 1px solid ${p => rgba(p.theme.colors.failureMedium, 0.7)};
  }

  ${minTabletMediaQuery} {
    max-width: 225px;
  }
`

export const InputError = styled.input`
  ${p => p.theme.typography.standard.size_15px.normal()}
  color: ${p => p.theme.colors.neutralExtraDark};
  background-color: transparent;
  width: 100%;
  border: none;

  ::placeholder {
    color: ${p => p.theme.colors.failureMedium};
  }

  :focus {
    outline: transparent;
  }
`

export const Button = styled.button`
  background-color: ${p => p.theme.colors.primaryMedium};
  ${p => p.theme.typography.standard.size_14px.semibold()}
  color: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.primaryMedium};
  border-radius: 2px;
  padding: 12px 40px;
  max-width: 800px;
  width: 100%;
  max-height: 50px;
  height: 100%;

  :hover {
    cursor: pointer;
    background-color: ${p => p.theme.colors.primaryDark};
  }

  :active {
    background-color: ${p => p.theme.colors.primaryExtraDark};
  }

  ${minTabletMediaQuery} {
    margin-left: 10px;
    max-width: 140px;
  }
`

export const SuccessMessage = styled.p<{ show: boolean }>`
  background-color: transparent;
  ${p => p.theme.typography.standard.size_14px.normal()}
  color: ${p => p.theme.colors.successMedium};
  border: 1px solid ${p => p.theme.colors.successMedium};
  border-radius: 2px;
  padding: 14px 10px;
  max-width: 320px;
  margin: 0 auto;
`

export const ErrorTooltip = styled.p<{ isVisible: boolean }>`
  background-color: ${p => p.theme.colors.failureExtraLight};
  border: 1px solid ${p => p.theme.colors.failureExtraLight};
  border-radius: 2px;
  ${p => p.theme.typography.standard.size_11px.normal()}
  color: ${p => p.theme.colors.failureMedium};
  padding: 4px 12px;
  margin: 0;
  position: relative;
  top: -10px;
  left: -5px;
  visibility: ${p => (p.isVisible ? "visible" : "hidden")};

  &::before {
    display: block;
    content: "";
    border: 8px solid transparent;
    border-bottom-color: ${p => p.theme.colors.failureExtraLight};
    margin: 0;
    width: 0;
    height: 0;
    position: relative;
    top: -22px;
    left: -15px;
  }
`

export const AlertIcon = styled.div`
  :hover {
    cursor: pointer;
  }
`

// export const Triangle = styled.div<{ isVisible: boolean }>`
//   visibility: ${p => (p.isVisible ? "visible" : "hidden")};
//   border: 8px solid transparent;
//   border-bottom-color: ${p => p.theme.colors.failureExtraLight};
//   margin: 0;
//   width: 0;
//   height: 0;
//   position: relative;
//   top: -22px;
//   left: -15px;
// `
